import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Typography, Stack } from '@/primitives';
import NameCell from '@/components/publicSearch/ResultCell/NameCell';
import { DEFAULT_MEMBER_DETAIL } from '@/studbook/store/useMemberFilterStore';
import { GetMemberDetailsResponse } from '@/studbook/graphql/results';
import { ENTITY_DETAIL_NAME } from '@/model/form-builder';
import { getMembershipInfoFromEntity } from '@/utils/entity-detail';
import { ContactDetailDto } from '@/apiClient';
import { parseJson } from '@/lib/common';
import { AddressField } from '@/components/formBuilder/form/form-fields/Address';
import { formatDate } from '@/lib/dateTime';
import { PUBLIC_ROUTES } from '@/configs/routes';
import { defaultPublicSearchVisibleFields } from './memberColumnConfig';

export type TRowValue = { value: string | number; id: string | number };
export type TRow = {
  [detailName: string]: TRowValue;
};

interface CreatePublicSearchMemberRenderCellOptions {
  orgName?: string;
}

/**
 * Creates a render cell function for public search member columns
 * This is specifically designed for the public search member filter context
 */
export function createPublicSearchMemberRenderCell(
  options: CreatePublicSearchMemberRenderCellOptions = {},
) {
  const { orgName } = options;

  return function renderCell(header: string, memberDetail?: any) {
    const CellRenderer = function (params: GridRenderCellParams<TRow>) {
      // Handle NAME column with special link rendering if orgName is available
      if (header === DEFAULT_MEMBER_DETAIL.NAME && orgName) {
        return (
          <NameCell
            name={params.row[header].value.toString()}
            path={`/${orgName}${PUBLIC_ROUTES.MEMBER}/${
              params.row[DEFAULT_MEMBER_DETAIL.ID].id
            }`}
          />
        );
      }

      // Handle special columns based on entity detail name
      switch (header) {
        case ENTITY_DETAIL_NAME.MEMBERSHIP: {
          const membershipInfo = getMembershipInfoFromEntity(
            params.row as any, // Cast to EntityDto-like structure
            memberDetail,
          );

          if (!membershipInfo?.Membership) {
            return membershipInfo?.MembershipValue || '';
          }

          return (
            <Stack
              sx={{
                flexDirection: 'row',
                gap: 1,
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Typography variant="bodymedium">
                <span>Type</span>: {membershipInfo.Membership?.name}
              </Typography>
              <Typography variant="bodymedium">
                <span>Since</span>:{' '}
                {formatDate(new Date(membershipInfo.EffectiveDate))}
              </Typography>
            </Stack>
          );
        }

        case ENTITY_DETAIL_NAME.ADDRESS: {
          const addressFields = parseJson(
            String(params.row[header]?.value || ''),
            [],
          ) as AddressField[];
          if (!addressFields.length) return '';
          const { city, country, postCode, state, streetAddress } =
            addressFields[0];
          const addressFiltered = [
            streetAddress,
            city,
            state,
            postCode,
            country,
          ].filter((field) => field);
          return addressFiltered.join(', ') || '';
        }

        case ENTITY_DETAIL_NAME.EMAIL: {
          const emailFields = parseJson(
            String(params.row[header]?.value || ''),
            [],
          ) as ContactDetailDto[];
          if (!emailFields.length) return '';
          let primaryIndex = emailFields.findIndex((p) => p.isPrimary);
          if (primaryIndex === -1) primaryIndex = 0;
          return emailFields[primaryIndex].contactValue || '';
        }

        case ENTITY_DETAIL_NAME.PHONE: {
          const phoneFields = parseJson(
            String(params.row[header]?.value || ''),
            [],
          ) as ContactDetailDto[];
          if (!phoneFields.length) return '';
          return phoneFields[0].contactValue || '';
        }
      }

      // Default rendering for all other columns
      return (
        <Typography variant="bodymedium">
          {(params.row[header]?.value ?? '').toString()}
        </Typography>
      );
    };

    CellRenderer.displayName = `PublicSearchMemberCell_${header}`;
    return CellRenderer;
  };
}

/**
 * Creates a column definition for public search member filter
 * This maintains the same structure as the original useMemberFilter implementation
 */
export function createPublicSearchMemberColumnDef(
  header: string,
  orgName?: string,
  headerName?: string,
  memberDetail?: any,
) {
  const renderCellFunction = createPublicSearchMemberRenderCell({ orgName });

  return {
    entityDetailName: memberDetail?.entityDetailName || '',
    field: header,
    headerClassName: header === DEFAULT_MEMBER_DETAIL.NAME ? 'pl' : '',
    headerName: headerName || header,
    minWidth: 150,
    sortable: true,
    renderCell: renderCellFunction(header, memberDetail),
  };
}

/**
 * Creates column definitions from member details response
 * By default, only shows the default visible columns.
 * All columns (including dynamic ones) are only returned when explicitly requested via includeAllColumns
 */
export function createMemberColumnsFromDetails(
  memberDetails: GetMemberDetailsResponse | undefined,
  orgName?: string,
) {
  // create columns for the default visible fields
  const defaultColumns = defaultPublicSearchVisibleFields.reduce(
    (cols, detailName) => {
      const memberDetail = memberDetails?.memberDetails?.find(
        (detail) => detail.entityDetailName === detailName,
      );
      if (memberDetail?.isDatabaseSearchEnable) {
        cols.push(
          createPublicSearchMemberColumnDef(
            detailName,
            orgName,
            memberDetail?.entityDetailConfigs?.labelText || detailName,
            memberDetail,
          ),
        );
      }

      return cols;
    },
    [] as ReturnType<typeof createPublicSearchMemberColumnDef>[],
  );

  // Create dynamic columns from member details (for column visibility support)
  const dynamicColumns = (memberDetails?.memberDetails || [])
    .filter(
      (detail) =>
        detail.entityDetailName &&
        detail.isDatabaseSearchEnable &&
        !defaultPublicSearchVisibleFields.includes(detail.entityDetailName),
    )
    .map((detail) =>
      createPublicSearchMemberColumnDef(
        detail.entityDetailName || '',
        orgName,
        detail.entityDetailConfigs?.labelText || detail.entityDetailName || '',
        detail,
      ),
    );

  return [...defaultColumns, ...dynamicColumns];
}
